import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { io } from 'socket.io-client';
import { fixImageUrls } from '../utils/imageUtils';
import CookieBanner, { hasCookieConsent } from './CookieBanner';
import { checkoutAPI, contactAPI, API_BASE_URL } from '../api';
import { validation } from '../utils/validation';
import { locationUtils } from '../utils/location';
import { notifications } from '../utils/notifications';
import AdminLogin from './admin/AdminLogin';
import * as analyticsTracking from '../utils/analyticsTracking';
import { groupProductsByBaseName, getSelectedVariant, isInStock } from '../utils/productHelpers';
import useSmoothScroll from '../hooks/useSmoothScroll';
import L, { addMapTilerLayer } from '../utils/leafletSetup';

import Header from './storefront/Header';
import Hero, { TrustBar } from './storefront/Hero';
import ProductGrid, { QuickAddToast, ImageZoomModal } from './storefront/ProductGrid';
import ProductQuickView from './storefront/ProductQuickView';
import CartDrawer from './storefront/CartDrawer';
import BrandStory from './storefront/BrandStory';
import ContactModal from './storefront/ContactModal';
import Footer from './storefront/Footer';
import CheckoutModal from './storefront/checkout/CheckoutModal';
import PaymentStatusModal from './storefront/checkout/PaymentStatusModal';

const AdvancedEcommerceWebsite = () => {
  const DEBUG_LOGS_ENABLED = process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true';
  const debugWarn = (...args) => { if (DEBUG_LOGS_ENABLED) console.warn(...args); };

  // ---- Core state ----
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceFilter, setPriceFilter] = useState([0, 10000]);
  const [categories, setCategories] = useState([]);
  const [isPlaying, setIsPlaying] = useState(true);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [wishlist, setWishlist] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showRecentlyViewed, setShowRecentlyViewed] = useState(false);
  const [viewedProducts, setViewedProducts] = useState([]);
  const [showQuickAdd, setShowQuickAdd] = useState(null);

  const [showPaymentStatus, setShowPaymentStatus] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentPollingInterval, setPaymentPollingInterval] = useState(null);
  const [activeSection, setActiveSection] = useState('home');
  const [showContactPage, setShowContactPage] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminLoginInitialError, setAdminLoginInitialError] = useState('');
  const [adminData, setAdminData] = useState(null);
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [customerInfo, setCustomerInfo] = useState({
    fullName: '', email: '', phone: '', address: '', city: '', county: '',
    country: 'Kenya', postalCode: '', location: null, coordinates: null,
  });

  const [checkoutFormData, setCheckoutFormData] = useState({
    fullName: '', email: '', phone: '', dateOfBirth: '', gender: '', company: '',
    address: '', city: '', county: '', country: 'Kenya', postalCode: '',
    paymentMethod: '', cardNumber: '', cardExpiry: '', cardCvv: '', cardName: '',
    bankName: '', accountNumber: '', mobileNumber: '', walletProvider: '', cryptoAddress: '',
    specialInstructions: '', deliveryNotes: '', marketingConsent: '', smsConsent: '',
    preferredLanguage: 'en', coordinates: null, locationData: null,
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Server-authoritative subtotal/shipping/costs/total — the same calculation
  // that actually charges the order, so the checkout display can never drift
  // from what the customer is really billed. Shipping is 0 until the backend
  // responds, since it depends on a delivery county not yet entered.
  const [checkoutTotals, setCheckoutTotals] = useState({ subtotal: 0, shipping: 0, costs: [], total: 0 });
  // 'map' (pin a location, address fields auto-fill) or 'manual' (type the address) —
  // mutually exclusive so the customer is never asked for the same details twice.
  const [locationEntryMode, setLocationEntryMode] = useState('map');
  const manualMapRef = useRef(null);
  const manualMapInstance = useRef(null);
  const manualMarkerRef = useRef(null);
  const manualCircleRef = useRef(null);
  // Populated by the map-setup effect below with a function the landmark
  // search box can call to move the pin to a searched place — kept as a ref
  // (not a prop drilled down) since the map instance itself only exists
  // inside that effect's closure.
  const applySearchResultRef = useRef(null);
  const [locationPrecisionWarning, setLocationPrecisionWarning] = useState(null);

  const [contactFormData, setContactFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [contactFormErrors, setContactFormErrors] = useState({});
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);

  const [userBehavior, setUserBehavior] = useState({
    userId: null, sessionId: null, timeSpent: {}, sectionsVisited: [], productsViewed: [],
    productsClicked: [], searchQueries: [], scrollDepth: 0, mouseActivity: 0, lastActivity: Date.now(),
  });
  const [recommendations, setRecommendations] = useState([]);
  const sectionsRef = useRef({});
  const lenisRef = useSmoothScroll();
  const smoothScrollTo = useCallback((element, options = {}) => {
    if (!element) return;
    if (lenisRef.current) {
      lenisRef.current.scrollTo(element, { offset: -80, duration: 1.1, ...options });
    } else {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [lenisRef]);

  const [apiSlides, setApiSlides] = useState([]);
  const [apiProducts, setApiProducts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStorefrontCategory, setSelectedStorefrontCategory] = useState('handbags');
  const [advancedFilters, setAdvancedFilters] = useState({ brand: 'all', material: 'all', minRating: 0, inStockOnly: false, sortBy: 'featured' });
  const [quickPreviewProduct, setQuickPreviewProduct] = useState(null);
  const [animatingCartItem, setAnimatingCartItem] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);

  // ---- Analytics + user tracking init ----
  useEffect(() => {
    if (hasCookieConsent()) analyticsTracking.initAnalytics();
  }, []);

  useEffect(() => {
    const generateUserId = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('DPC User ID', 2, 2);
      const fingerprint = [
        navigator.userAgent, navigator.language,
        window.screen.width + 'x' + window.screen.height,
        new Date().getTimezoneOffset(), canvas.toDataURL(),
      ].join('|');
      return btoa(fingerprint).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
    };
    const generateSessionId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

    const userId = localStorage.getItem('dpc_user_id') || generateUserId();
    const sessionId = generateSessionId();
    localStorage.setItem('dpc_user_id', userId);
    sessionStorage.setItem('dpc_session_id', sessionId);
    setUserBehavior((prev) => ({ ...prev, userId, sessionId, lastActivity: Date.now() }));

    const determineRegionFromCoordinates = (lat, lng) => {
      const nairobiLat = -1.2921, nairobiLng = 36.8219;
      const distanceKm = Math.sqrt(Math.pow(lat - nairobiLat, 2) + Math.pow(lng - nairobiLng, 2)) * 111;
      if (distanceKm < 50) return 'nairobi';
      if (distanceKm < 100) return 'central';
      if (distanceKm < 200) return 'eastern';
      if (distanceKm < 300) return 'rift';
      if (distanceKm < 400) return 'western';
      if (distanceKm < 500) return 'coast';
      if (distanceKm < 600) return 'nyanza';
      return 'northeastern';
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const region = determineRegionFromCoordinates(latitude, longitude);
          setCustomerInfo((prev) => ({ ...prev, coordinates: { latitude, longitude }, location: region }));
        },
        () => {
          setCustomerInfo((prev) => ({ ...prev, location: 'nairobi', coordinates: { latitude: -1.2921, longitude: 36.8219 } }));
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    } else {
      setCustomerInfo((prev) => ({ ...prev, location: 'nairobi', coordinates: { latitude: -1.2921, longitude: 36.8219 } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Delivery location map (Leaflet + MapTiler tiles) ----
  useEffect(() => {
    if (locationEntryMode !== 'map' || !manualMapRef.current || manualMapInstance.current) return;

    function geocodeAndApply(lat, lng, onDone) {
      const coordinates = { latitude: lat, longitude: lng };
      setCheckoutFormData((prev) => ({ ...prev, coordinates }));
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Geocoding timeout')), 10000));
      Promise.race([
        fetch(`${API_BASE_URL}/api/geocode`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lat, lng }),
        }).then((res) => { if (!res.ok) throw new Error(`Geocoding failed: ${res.status}`); return res.json(); }),
        timeoutPromise,
      ]).then((geocodeData) => {
        const geocodingSucceeded = geocodeData && !geocodeData.geocodingFailed && (geocodeData.address || geocodeData.city);
        const matchedCounty = geocodeData.county ? matchCountyName(geocodeData.county) : '';
        const newCountry = geocodeData.country || checkoutFormData.country || 'Kenya';
        setCheckoutFormData((prev) => ({
          ...prev,
          address: geocodeData.address || prev.address || '',
          city: geocodeData.city || prev.city || '',
          county: matchedCounty || prev.county || '',
          country: newCountry,
          postalCode: geocodeData.postalCode || prev.postalCode || '',
          locationData: geocodeData,
          coordinates,
        }));
        notifications.showSuccess(geocodingSucceeded ? 'Location set! Address details updated.' : 'Location coordinates saved! Please fill address details manually.');
        if (onDone) onDone();
      }).catch(() => {
        setCheckoutFormData((prev) => ({ ...prev, coordinates, locationData: { lat, lng, source: 'manual', error: 'Geocoding failed but coordinates saved' } }));
        notifications.showSuccess('Location coordinates saved! Please fill address manually.');
        if (onDone) onDone();
      });
    }

    const defaultCenter = checkoutFormData.coordinates
      ? [checkoutFormData.coordinates.latitude, checkoutFormData.coordinates.longitude]
      : [-1.2921, 36.8219];

    const map = L.map(manualMapRef.current).setView(defaultCenter, 13);
    addMapTilerLayer(map);

    // A manually placed/dragged pin is exact by definition — the user put
    // it there on purpose — so it always clears any uncertainty radius/
    // warning left over from a prior wide-area search match.
    const clearRadiusCircle = () => {
      if (manualCircleRef.current) { manualCircleRef.current.remove(); manualCircleRef.current = null; }
      setLocationPrecisionWarning(null);
    };

    let marker = null;
    const attachDragHandler = (m) => {
      m.on('dragend', () => { const p = m.getLatLng(); clearRadiusCircle(); geocodeAndApply(p.lat, p.lng); });
    };

    if (checkoutFormData.coordinates) {
      marker = L.marker(defaultCenter, { draggable: true, title: 'Your delivery location' }).addTo(map);
      attachDragHandler(marker);
      manualMarkerRef.current = marker;
    }

    map.on('click', (e) => {
      const { lat, lng } = e.latlng;
      clearRadiusCircle();
      if (marker) {
        marker.setLatLng([lat, lng]);
      } else {
        marker = L.marker([lat, lng], { draggable: true, title: 'Your delivery location' }).addTo(map);
        attachDragHandler(marker);
        manualMarkerRef.current = marker;
      }
      geocodeAndApply(lat, lng);
    });

    // Called by the landmark search box when the user picks a result — see
    // LocationSearchBox / /api/geocode/search. Unlike a click/drag, this
    // carries a radius: MapTiler's match might be a precise address (small,
    // default 200m) or a whole town/area (the backend computes a radius
    // from the match's bounding box), so this draws that as a visible
    // circle and surfaces a warning for the imprecise case — the same
    // "it could be up to Xkm from the pin" pattern the Thamini reference
    // uses, rather than silently pretending a town-level match is exact.
    applySearchResultRef.current = (lat, lng, radiusMeters, isWideArea, placeName) => {
      if (manualCircleRef.current) manualCircleRef.current.remove();

      if (marker) {
        marker.setLatLng([lat, lng]);
      } else {
        marker = L.marker([lat, lng], { draggable: true, title: 'Your delivery location' }).addTo(map);
        attachDragHandler(marker);
        manualMarkerRef.current = marker;
      }

      manualCircleRef.current = L.circle([lat, lng], {
        radius: radiusMeters,
        color: isWideArea ? '#A2401E' : '#7A2E15',
        fillColor: isWideArea ? '#A2401E' : '#7A2E15',
        fillOpacity: 0.12,
        weight: 1.5,
      }).addTo(map);

      map.fitBounds(manualCircleRef.current.getBounds(), { maxZoom: isWideArea ? 15 : 17 });

      setLocationPrecisionWarning(
        isWideArea
          ? `${placeName} is a wide area: the delivery point could be up to ${(radiusMeters / 1000).toFixed(1)}km from the pin. Drag the pin to the exact spot, or search for a more specific street or landmark.`
          : null
      );

      geocodeAndApply(lat, lng);
    };

    manualMapInstance.current = map;

    return () => {
      map.remove();
      manualMapInstance.current = null;
      manualMarkerRef.current = null;
      manualCircleRef.current = null;
      applySearchResultRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationEntryMode]);

  // ---- Payment status: one-shot check ----
  const checkPaymentOnce = async (checkoutRequestID, orderId = null) => {
    try {
      const statusUrl = orderId
        ? `${API_BASE_URL}/api/payments/status/${checkoutRequestID}?orderId=${orderId}`
        : `${API_BASE_URL}/api/payments/status/${checkoutRequestID}`;
      const dbResponse = await fetch(statusUrl);
      if (dbResponse.ok) {
        const dbResult = await dbResponse.json();
        const payment = dbResult.payment || dbResult;
        if (payment && payment.status === 'COMPLETED') {
          setPaymentStatus((prev) => ({ ...prev, status: 'success' }));
          notifications.showSuccess('Payment successful! Your order has been confirmed.');
          setCartItems([]);
          setTimeout(() => { setShowPaymentStatus(false); setCheckoutStep(3); }, 800);
          return;
        }
      }
      const response = await fetch(`${API_BASE_URL}/api/mpesa/query`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ checkoutRequestID }),
      });
      const result = await response.json();
      const rawCode = result?.result?.ResultCode ?? result?.result?.resultCode ?? null;
      const code = rawCode !== null && rawCode !== undefined ? parseInt(rawCode, 10) : null;

      if (result.success && result.result && code === 0) {
        setPaymentStatus((prev) => ({ ...prev, status: 'success' }));
        notifications.showSuccess('Payment successful! Your order has been confirmed.');
        setCartItems([]);
        setTimeout(() => { setShowPaymentStatus(false); setCheckoutStep(3); }, 800);
        return;
      }
      if (code === 2003) {
        notifications.showWarning('Still insufficient balance. Top up your wallet then tap "Check again".');
        setPaymentStatus((prev) => ({ ...prev, status: 'insufficient_balance', lastResultCode: code, failureReason: 'Insufficient M-Pesa balance. Please top up your wallet and check again.' }));
        return;
      }
      if (code === 1032 || code === 1) {
        setPaymentStatus((prev) => ({
          ...prev, status: code === 1032 ? 'pin_pending' : 'pending',
          message: code === 1032 ? 'Please complete M-Pesa PIN authorization on your phone.' : 'Transaction is being processed by M-Pesa. Please wait...',
        }));
        return;
      }
      const fallbackMessage = result?.result?.ResultDesc || result.message || 'Payment is still pending or failed.';
      setPaymentStatus((prev) => ({ ...prev, status: 'failed', failureReason: fallbackMessage }));
      notifications.showError(fallbackMessage);
    } catch (err) {
      debugWarn('checkPaymentOnce error:', err);
      notifications.showError('Unable to check payment at the moment. Try again.');
    }
  };

  const trackUserBehavior = (action, data = {}) => {
    setUserBehavior((prev) => {
      const newItem = (typeof data === 'string' || typeof data === 'number') ? data : { ...data, timestamp: Date.now() };
      return { ...prev, [action]: [...(prev[action] || []), newItem], lastActivity: Date.now() };
    });
  };

  // ---- Scroll / active-section tracking ----
  useEffect(() => {
    let timeoutId;
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const currentScrollY = window.scrollY;
        const scrollDepth = Math.round((currentScrollY / (documentHeight - windowHeight)) * 100);
        setUserBehavior((prev) => ({ ...prev, scrollDepth: Math.max(prev.scrollDepth, scrollDepth), lastActivity: Date.now() }));

        const sections = ['home', 'products', 'about', 'testimonials', 'contact'];
        let currentSection = 'home';
        for (const section of sections) {
          const element = sectionsRef.current[section];
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 100 && rect.bottom >= 100) { currentSection = section; break; }
          }
        }
        const isNearBottom = currentScrollY + windowHeight >= documentHeight - 50;
        if (isNearBottom && currentSection === 'home') currentSection = 'contact';
        setActiveSection(currentSection);
      }, 16);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleMouseMoveTracking = () => {
      setUserBehavior((prev) => ({ ...prev, mouseActivity: prev.mouseActivity + 1, lastActivity: Date.now() }));
    };
    window.addEventListener('mousemove', handleMouseMoveTracking, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMoveTracking);
  }, []);

  useEffect(() => {
    const startTime = Date.now();
    return () => {
      const timeSpent = Date.now() - startTime;
      setUserBehavior((prev) => ({ ...prev, timeSpent: { ...prev.timeSpent, [activeSection]: (prev.timeSpent[activeSection] || 0) + timeSpent } }));
    };
  }, [activeSection]);

  const activeApiProducts = useMemo(() => apiProducts.filter((p) => p.isActive !== false), [apiProducts]);

  // ---- Personalized recommendations ----
  const calculateRelevanceScore = (product, behavior) => {
    let score = 0;
    if (behavior.productsViewed.some((p) => p.category === product.category)) score += 10;
    const preferredColors = behavior.productsViewed.map((p) => p.color).filter(Boolean);
    if (product.variants && product.variants.length > 0) {
      if (product.variants.some((v) => preferredColors.includes(v.color))) score += 5;
    } else {
      const productColors = product.colors || [];
      if (Array.isArray(productColors) && productColors.some((color) => preferredColors.includes(color))) score += 5;
    }
    const viewedPrices = behavior.productsViewed.map((p) => p.price).filter(Boolean);
    if (viewedPrices.length > 0) {
      const avgPrice = viewedPrices.reduce((a, b) => a + b, 0) / viewedPrices.length;
      const productPrice = (product.variants && product.variants.length > 0) ? (product.variants[0]?.price || 0) : (product.price || 0);
      const priceDiff = Math.abs(productPrice - avgPrice) / avgPrice;
      score += Math.max(0, 5 - priceDiff * 10);
    }
    behavior.searchQueries.forEach((query) => {
      let queryString = typeof query === 'string' ? query : (typeof query === 'object' && query.query ? query.query : '');
      if (typeof queryString !== 'string' || !queryString) return;
      const productName = product.name || product.baseName || '';
      const productDescription = product.description || '';
      const productMaterial = product.material || '';
      const productCategory = product.category || '';
      if ([productName, productDescription, productMaterial, productCategory].some((f) => f.toLowerCase().includes(queryString.toLowerCase()))) score += 3;
    });
    if (behavior.timeSpent.products > 30000) score += 2;
    return score;
  };

  const generatePersonalizedRecommendations = useCallback(() => {
    const { productsViewed = [] } = userBehavior;
    const categoryPreferences = {}, colorPreferences = {};
    const pricePreferences = { min: Infinity, max: 0 };
    productsViewed.forEach((product) => {
      if (product.category) categoryPreferences[product.category] = (categoryPreferences[product.category] || 0) + 1;
      if (product.color) colorPreferences[product.color] = (colorPreferences[product.color] || 0) + 1;
      if (product.price) {
        pricePreferences.min = Math.min(pricePreferences.min, product.price);
        pricePreferences.max = Math.max(pricePreferences.max, product.price);
      }
    });
    const personalized = activeApiProducts
      .filter((product) => {
        const hasPreferredCategory = Object.keys(categoryPreferences).length === 0 || categoryPreferences[product.category] > 0;
        const hasPreferredColor = Object.keys(colorPreferences).length === 0 || (product.colors && product.colors.some((color) => colorPreferences[color]));
        const hasPreferredPrice = pricePreferences.min === Infinity || (product.price >= pricePreferences.min * 0.8 && product.price <= pricePreferences.max * 1.2);
        return hasPreferredCategory && hasPreferredColor && hasPreferredPrice;
      })
      .sort((a, b) => calculateRelevanceScore(b, userBehavior) - calculateRelevanceScore(a, userBehavior))
      .slice(0, 8);
    setRecommendations(personalized);
  }, [userBehavior, activeApiProducts]);

  useEffect(() => {
    const hasSignals = (userBehavior.productsViewed?.length > 0) || (userBehavior.searchQueries?.length > 0) || (userBehavior.productsClicked?.length > 0);
    if (hasSignals) generatePersonalizedRecommendations();
  }, [userBehavior.productsViewed, userBehavior.searchQueries, userBehavior.productsClicked, generatePersonalizedRecommendations]);

  // ---- Totals ----
  // Item subtotal only — this is all the cart drawer shows. Shipping and any
  // additional costs depend on the delivery county, which isn't known until
  // checkout, so they're calculated and shown there (see checkoutTotals below)
  // instead of as a guess in the cart that could disagree with the real charge.
  const calculateCartTotal = useCallback(() => cartItems.reduce((total, item) => total + (item.price * item.quantity), 0), [cartItems]);

  const handleCheckout = () => {
    if (cartItems.length === 0) return;
    setShowCheckout(true);
    setCheckoutStep(1);
    const cartValue = calculateCartTotal();
    analyticsTracking.trackCheckoutStart(cartValue, cartItems.length);
    analyticsTracking.trackFunnelStage('checkout_start', 'add_to_cart', null, null, cartItems.map((item) => item.id), cartValue);
    setCheckoutFormData((prev) => ({
      ...prev, fullName: customerInfo.fullName || '', email: customerInfo.email || '', phone: customerInfo.phone || '',
      address: customerInfo.address || '', city: customerInfo.city || '', county: customerInfo.county || '', country: customerInfo.country || 'Kenya',
    }));
  };

  const createInputHandler = useCallback((field) => (value) => {
    setCheckoutFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const inputHandlers = useMemo(() => ({
    fullName: createInputHandler('fullName'), email: createInputHandler('email'), phone: createInputHandler('phone'),
    address: createInputHandler('address'), city: createInputHandler('city'), dateOfBirth: createInputHandler('dateOfBirth'),
    gender: createInputHandler('gender'), company: createInputHandler('company'), postalCode: createInputHandler('postalCode'),
    specialInstructions: createInputHandler('specialInstructions'), deliveryNotes: createInputHandler('deliveryNotes'),
    marketingConsent: createInputHandler('marketingConsent'), smsConsent: createInputHandler('smsConsent'),
    paymentMethod: createInputHandler('paymentMethod'), cardNumber: createInputHandler('cardNumber'), cardName: createInputHandler('cardName'),
    cardExpiry: createInputHandler('cardExpiry'), cardCvv: createInputHandler('cardCvv'), cardBrand: createInputHandler('cardBrand'),
    bankName: createInputHandler('bankName'), accountNumber: createInputHandler('accountNumber'), routingNumber: createInputHandler('routingNumber'),
    walletType: createInputHandler('walletType'), walletId: createInputHandler('walletId'), mobileNumber: createInputHandler('mobileNumber'),
    walletProvider: createInputHandler('walletProvider'), cryptoAddress: createInputHandler('cryptoAddress'),
  }), [createInputHandler]);

  const handleLocationChange = useCallback((field, value) => {
    setCheckoutFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const matchCountyName = (googleCounty) => {
    if (!googleCounty) return '';
    const normalized = googleCounty.replace(/\s+County$/i, '').replace(/\s+Province$/i, '').trim();
    const exactMatch = locationUtils.counties.find((county) => county.toLowerCase() === normalized.toLowerCase());
    if (exactMatch) return exactMatch;
    const partialMatch = locationUtils.counties.find((county) => county.toLowerCase().includes(normalized.toLowerCase()) || normalized.toLowerCase().includes(county.toLowerCase()));
    return partialMatch || normalized;
  };

  // Fetch the real subtotal/shipping/costs/total from the backend whenever the
  // cart or delivery county/country changes — the same calculation the order
  // is actually charged with, so checkout never shows a number that doesn't
  // match what gets billed.
  useEffect(() => {
    if (cartItems.length === 0 || !showCheckout) return;
    let cancelled = false;
    const fetchTotals = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/cart/calculate-totals`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartItems: cartItems.map((item) => ({ id: item.productGroupId || item.id, quantity: item.quantity })),
            country: checkoutFormData.country,
            county: checkoutFormData.county,
          }),
        });
        if (response.ok && !cancelled) setCheckoutTotals(await response.json());
      } catch (error) {
        console.error('Error calculating checkout totals:', error);
      }
    };
    fetchTotals();
    return () => { cancelled = true; };
  }, [cartItems, checkoutFormData.country, checkoutFormData.county, showCheckout]);

  // ---- Checkout submission ----
  // Gates the Review -> Payment step transition — previously unvalidated
  // entirely, so a user could reach the payment screen with a blank name/
  // phone/address and only find out something was wrong after attempting
  // payment, by which point the broken fields were no longer even visible.
  const handleContinueToPayment = () => {
    const validationResult = validation.validateCheckoutStep1(checkoutFormData);
    if (!validationResult.isValid) {
      setFormErrors(validationResult.errors);
      notifications.showError('Please fill in all required fields');
      return;
    }
    setFormErrors({});
    setCheckoutStep(2);
  };

  const validateAndSubmitCheckout = async () => {
    const validationResult = validation.validateCheckoutForm(checkoutFormData);
    if (!validationResult.isValid) {
      setFormErrors(validationResult.errors);
      notifications.showError('Please fix the errors in the form');
      return;
    }
    setFormErrors({});
    setIsSubmitting(true);
    notifications.showLoading('Processing your order...');

    try {
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substr(2, 5).toUpperCase();
      const orderId = `DPC-${timestamp}-${randomId}`;
      const idempotencyKey = `${checkoutFormData.email}-${timestamp}-${cartItems.map((i) => i.id).join('-')}`;

      const resolveCanonicalProductId = (item) => {
        if (!item) return null;
        if (item.productGroupId) return item.productGroupId;
        if (item.productId) return item.productId;
        const rawId = typeof item.id === 'string' ? item.id : '';
        if (!rawId) return null;
        if (rawId.startsWith('var-')) return rawId;
        if (rawId.includes('-')) return rawId.split('-')[0] || null;
        return rawId;
      };

      const processedCartItems = cartItems.map((item) => {
        const canonicalProductId = resolveCanonicalProductId(item);
        if (!canonicalProductId) return null;
        return {
          id: canonicalProductId, productId: canonicalProductId,
          variantId: item.variantId || item.id || `${canonicalProductId}-${item.selectedColor || 'default'}`,
          name: item.name, price: item.price, quantity: item.quantity,
          color: item.selectedColor || item.color, colorCode: item.selectedColorCode || item.colorCode,
          image: item.image, material: item.material, category: item.category, inStock: isInStock(item),
          originalPrice: item.originalPrice, cartLineId: item.cartLineId, productGroupId: item.productGroupId || canonicalProductId,
        };
      }).filter(Boolean);

      if (processedCartItems.length === 0) {
        notifications.showError('No valid products in cart. Please re-add items from product page and try again.');
        setIsSubmitting(false);
        return;
      }

      const coordinates = checkoutFormData.coordinates || null;
      const locationData = checkoutFormData.locationData || null;

      const orderData = {
        idempotencyKey,
        customerInfo: {
          fullName: checkoutFormData.fullName, email: checkoutFormData.email, phone: checkoutFormData.phone,
          address: checkoutFormData.address || '', city: checkoutFormData.city || '', county: checkoutFormData.county || '',
          country: checkoutFormData.country || 'Kenya', postalCode: checkoutFormData.postalCode || '',
          coordinates, locationData: locationData ? (typeof locationData === 'string' ? locationData : JSON.stringify(locationData)) : null,
          dateOfBirth: checkoutFormData.dateOfBirth || null, gender: checkoutFormData.gender || null,
          company: checkoutFormData.company || null, preferredLanguage: checkoutFormData.preferredLanguage || 'en',
        },
        cartItems: processedCartItems, totals: checkoutTotals, paymentMethod: checkoutFormData.paymentMethod,
        specialInstructions: checkoutFormData.specialInstructions, deliveryNotes: checkoutFormData.deliveryNotes,
        marketingConsent: checkoutFormData.marketingConsent, smsConsent: checkoutFormData.smsConsent,
      };

      if (checkoutFormData.paymentMethod === 'MPESA') {
        const phoneNumber = checkoutFormData.mobileNumber || checkoutFormData.phone;
        const paymentAmount = Math.round(orderData.totals.total);
        const response = await fetch(`${API_BASE_URL}/api/mpesa/stkpush`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phoneNumber, amount: paymentAmount, orderId, customerInfo: orderData.customerInfo, cartItems: orderData.cartItems }),
        });
        const result = await response.json();

        if (result.success) {
          notifications.hideLoading();
          if (result.isDuplicate) {
            notifications.showWarning(result.message || 'Order already exists. Please complete payment for existing order.');
            if (result.order?.orderNumber) {
              setShowPaymentStatus(true);
              setPaymentStatus({ status: 'pending', orderId: result.order.orderNumber, paymentMethod: 'MPESA', amount: orderData.totals.total, message: 'Completing payment for existing order...' });
              try {
                const orderResponse = await fetch(`${API_BASE_URL}/api/orders/${result.order.id}`);
                if (orderResponse.ok) {
                  const fetchedOrder = await orderResponse.json();
                  const latestPayment = fetchedOrder.payments?.[fetchedOrder.payments.length - 1];
                  if (latestPayment?.reference) pollPaymentStatus(latestPayment.reference, result.order.id);
                }
              } catch (err) { debugWarn('Could not fetch existing order payment:', err); }
            }
            return;
          }
          notifications.showSuccess('M-Pesa prompt sent! Please check your phone and enter your M-Pesa PIN.');
          if (paymentPollingInterval) { clearInterval(paymentPollingInterval); setPaymentPollingInterval(null); }
          setShowPaymentStatus(true);
          setPaymentStatus({ status: 'pending', checkoutRequestID: result.checkoutRequestID, merchantRequestID: result.merchantRequestID, orderId: result.orderId, phoneNumber: result.formattedPhone, amount: paymentAmount, paymentMethod: 'MPESA' });
          analyticsTracking.trackPaymentInitiated('MPESA', paymentAmount);
          analyticsTracking.trackFunnelStage('payment_initiated', 'checkout_start', null, null, cartItems.map((item) => item.id), paymentAmount);
          try { pollPaymentStatus(result.checkoutRequestID, result.orderId); } catch (pollError) { debugWarn('Polling started with potential issues, but STK Push was successful'); }
        } else {
          notifications.hideLoading();
          notifications.showError(result.message || 'Failed to initiate M-Pesa payment');
        }
      } else if (checkoutFormData.paymentMethod === 'CREDIT_CARD') {
        try {
          const paymentIntentResult = await checkoutAPI.createStripePaymentIntent(orderData.totals.total, orderId, customerInfo.email, customerInfo.email);
          if (paymentIntentResult.success && paymentIntentResult.clientSecret) {
            analyticsTracking.trackPaymentInitiated('CREDIT_CARD', orderData.totals.total);
            analyticsTracking.trackFunnelStage('payment_initiated', 'checkout_start', null, null, cartItems.map((item) => item.id), orderData.totals.total);
            const orderResponse = await checkoutAPI.processCheckout(orderData);
            const confirmResult = await checkoutAPI.confirmStripePayment(paymentIntentResult.paymentIntentId);
            notifications.hideLoading();
            if (confirmResult.success) {
              notifications.showSuccess('Payment processed successfully! Your order is confirmed.');
              const orderNumber = orderResponse.orderNumber || orderId || 'Unknown';
              analyticsTracking.trackPurchase(orderResponse.id || orderId || 'Unknown', orderNumber, orderData.totals.total, cartItems.length, cartItems.map((item) => ({ id: item.id, name: item.name, quantity: item.quantity })));
              analyticsTracking.trackFunnelStage('purchase', 'payment_initiated', null, null, cartItems.map((item) => item.id), orderData.totals.total);
              setCheckoutStep(3);
              setCartItems([]);
            } else {
              notifications.showWarning('Order created but payment is pending. We will contact you.');
              setShowPaymentStatus(true);
              setPaymentStatus({ status: 'pending', orderId: orderResponse.orderNumber || orderId, paymentMethod: 'CREDIT_CARD', amount: orderData.totals.total, message: 'Card payment is being processed' });
            }
          } else {
            throw new Error('Failed to create payment intent');
          }
        } catch (stripeError) {
          notifications.hideLoading();
          console.error('Stripe payment error:', stripeError);
          try {
            const orderResponse = await checkoutAPI.processCheckout(orderData);
            notifications.showWarning('Order created but payment processing failed. Please contact us at +254111566826 or info@digitalpulseke.com');
            setShowPaymentStatus(true);
            setPaymentStatus({ status: 'pending', orderId: orderResponse.orderNumber || orderId, paymentMethod: 'CREDIT_CARD', amount: orderData.totals.total, message: 'Payment processing failed. Please contact support.' });
          } catch (orderError) {
            notifications.showError('Failed to process payment. Please try again or contact support.');
          }
        }
      } else {
        const response = await checkoutAPI.processCheckout(orderData);
        notifications.hideLoading();
        if (checkoutFormData.paymentMethod === 'CASH_ON_DELIVERY') {
          notifications.showSuccess('Order confirmed! You will pay when your order is delivered.');
          const orderNumber = response.orderNumber || orderId || 'Unknown';
          analyticsTracking.trackPurchase(response.id || orderId || 'Unknown', orderNumber, orderData.totals.total, cartItems.length, cartItems.map((item) => ({ id: item.id, name: item.name, quantity: item.quantity })));
          analyticsTracking.trackFunnelStage('purchase', 'payment_initiated', null, null, cartItems.map((item) => item.id), orderData.totals.total);
          setCheckoutStep(3);
          setCartItems([]);
        } else {
          notifications.showSuccess('Order placed successfully! Payment is being processed.');
          setShowPaymentStatus(true);
          setPaymentStatus({ status: 'pending', orderId: response.orderNumber || 'Unknown', paymentMethod: checkoutFormData.paymentMethod, amount: orderData.totals.total, message: getPaymentMessage(checkoutFormData.paymentMethod) });
        }
      }
    } catch (error) {
      notifications.hideLoading();
      if (error.message?.includes('fetch') || error.message?.includes('network')) notifications.showError('Network error. Please check your connection and try again.');
      else notifications.showError('Failed to process order. Please try again.');
      console.error('Checkout error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactInputChange = (field, value) => {
    setContactFormData((prev) => ({ ...prev, [field]: value }));
    if (contactFormErrors[field]) setContactFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const validationResult = validation.validateContactForm(contactFormData);
    if (!validationResult.isValid) {
      setContactFormErrors(validationResult.errors);
      notifications.showError('Please fix the errors in the form');
      return;
    }
    setIsSubmittingContact(true);
    notifications.showLoading('Sending your message...');
    try {
      await contactAPI.submitContactForm(contactFormData);
      notifications.hideLoading();
      notifications.showSuccess("Message sent successfully! We'll get back to you soon. You can also reach us at +254111566826 or info@digitalpulseke.com");
      setContactFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      notifications.hideLoading();
      notifications.showError('Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setIsSubmittingContact(false);
    }
  };

  // ---- Admin ----
  useEffect(() => {
    if (sessionStorage.getItem('adminSessionExpired')) {
      sessionStorage.removeItem('adminSessionExpired');
      setAdminLoginInitialError('Your session expired. Please log in again.');
      setShowAdminLogin(true);
    }
  }, []);

  const handleAdminLogin = () => { setAdminLoginInitialError(''); setShowAdminLogin(true); };
  const handleAdminLoginSuccess = (admin) => {
    setAdminData(admin);
    setShowAdminLogin(false);
    const dashboardUrl = `${window.location.origin}/#admin-dashboard`;
    const popup = window.open(dashboardUrl, '_blank', 'width=1400,height=900,scrollbars=yes,resizable=yes');
    if (!popup) window.location.href = dashboardUrl;
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const storedAdminData = localStorage.getItem('adminData');
    if (token && storedAdminData) {
      try { setAdminData(JSON.parse(storedAdminData)); } catch (error) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminData');
      }
    }
  }, []);

  const scrollToSection = (sectionId) => {
    trackUserBehavior('sectionsVisited', { section: sectionId });
    if (sectionId === 'contact') { setShowContactPage(true); return; }
    smoothScrollTo(sectionsRef.current[sectionId]);
  };

  const handleSlideAction = (action, fallbackSection) => {
    if (action) {
      if (action.startsWith('category:')) {
        const targetCategory = action.replace('category:', '');
        setSelectedCategory(targetCategory);
        smoothScrollTo(sectionsRef.current.products);
      } else if (action.startsWith('scroll:')) {
        smoothScrollTo(sectionsRef.current[action.replace('scroll:', '')]);
      } else if (action.startsWith('url:')) {
        window.open(action.replace('url:', ''), '_blank');
      }
    } else {
      scrollToSection(fallbackSection);
    }
  };

  useEffect(() => {
    document.body.style.overflow = showContactPage ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [showContactPage]);

  // ---- Data loading ----
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [slidesRes, bagsRes, colorsRes, categoriesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/hero-slides`),
          fetch(`${API_BASE_URL}/api/products?category=all`),
          fetch(`${API_BASE_URL}/api/colors`),
          fetch(`${API_BASE_URL}/api/categories`),
        ]);
        if (!slidesRes.ok) throw new Error('Failed to load slides');
        if (!bagsRes.ok) throw new Error('Failed to load products');
        if (!colorsRes.ok) throw new Error('Failed to load colors');
        const [slidesJson, bagsJson] = await Promise.all([slidesRes.json(), bagsRes.json(), colorsRes.json()]);
        const categoriesJson = await categoriesRes.json();

        setApiSlides(fixImageUrls(Array.isArray(slidesJson) ? slidesJson : []));
        setApiProducts(fixImageUrls(Array.isArray(bagsJson) ? bagsJson : []));
        setCategories(Array.isArray(categoriesJson) ? categoriesJson : []);
      } catch (e) {
        console.error('Failed to load data', e);
        setError(e?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const refreshHeroSlides = async () => {
    try {
      const slidesRes = await fetch(`${API_BASE_URL}/api/hero-slides`);
      if (slidesRes.ok) {
        const slidesJson = await slidesRes.json();
        setApiSlides(fixImageUrls(Array.isArray(slidesJson) ? slidesJson : []));
      }
    } catch (error) {
      console.error('Failed to refresh hero slides:', error);
    }
  };

  const refreshProducts = async () => {
    try {
      const bagsRes = await fetch(`${API_BASE_URL}/api/products?category=all`);
      if (bagsRes.ok) {
        const bagsJson = await bagsRes.json();
        setApiProducts(fixImageUrls(Array.isArray(bagsJson) ? bagsJson : []));
      }
    } catch (error) {
      console.error('Failed to refresh products:', error);
    }
  };

  useEffect(() => {
    const interval = setInterval(refreshHeroSlides, 30000);
    const handleVisibilityChange = () => { if (!document.hidden) refreshHeroSlides(); };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', handleVisibilityChange); };
  }, []);

  // Live refresh: an admin editing a product/hero-slide (web or mobile)
  // pushes a 'catalog:update' event over the backend's unauthenticated
  // /public socket namespace, so an already-open storefront tab picks up
  // the change without needing a manual reload. Debounced, since a bulk
  // edit can fire several events in quick succession and each one only
  // needs to trigger one refetch, not one per event.
  useEffect(() => {
    const socket = io(`${API_BASE_URL || window.location.origin}/public`, {
      transports: ['websocket', 'polling'],
    });

    let debounceTimer = null;
    socket.on('catalog:update', (update) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (update?.type === 'heroSlide') refreshHeroSlides();
        else refreshProducts(); // default to products — covers 'product' and any future type
      }, 400);
    });

    return () => {
      clearTimeout(debounceTimer);
      socket.disconnect();
    };
  }, []);

  const displayedSlides = apiSlides;
  const groupedProducts = useMemo(() => groupProductsByBaseName(activeApiProducts), [activeApiProducts]);
  const displayedProducts = groupedProducts;

  const handleMouseMove = () => {}; // parallax target retained for prop-compat; no bound element in the new hero

  useEffect(() => {
    if (isPlaying && displayedSlides.length > 0) {
      const timer = setInterval(() => setCurrentSlide((prev) => (prev + 1) % displayedSlides.length), 6000);
      return () => clearInterval(timer);
    }
  }, [isPlaying, displayedSlides.length]);

  // ---- Cart ----
  const addToCart = (productGroup) => {
    const productGroupId = productGroup.productId || productGroup.id;
    let variantToAdd = getSelectedVariant(productGroup);
    const selectedColor = productGroup.selectedColor || productGroup.color || variantToAdd.color;
    const selectedColorCode = productGroup.selectedColorCode || productGroup.colorCode || variantToAdd.colorCode;
    const selectedImageId = productGroup.selectedImageId;
    const selectedColorName = productGroup.colorName || selectedColor;

    let matchingVariant = variantToAdd;
    if (selectedColor && productGroup.variants) {
      const found = productGroup.variants.find((v) => v.color === selectedColor || v.colorCode === selectedColorCode);
      if (found) matchingVariant = found;
    }

    let availableColors = [];
    const imageColorMaps = productGroup.imageColorMaps || [];
    if (imageColorMaps.length > 0) {
      availableColors = imageColorMaps.map((m, idx) => ({ id: m.id || `map-${idx}`, imageId: m.imageId, color: m.colorName, colorName: m.colorName, colorCode: m.colorCode || '#CCCCCC', imageUrl: m.imageUrl }));
    } else if (productGroup.colors && productGroup.colors.length > 0) {
      availableColors = productGroup.colors.map((c, idx) => ({ id: `color-${idx}`, color: c.name || c.color || selectedColor, colorCode: c.code || c.colorCode || selectedColorCode || '#CCCCCC' }));
    } else if (productGroup.variants && productGroup.variants.length > 0) {
      availableColors = productGroup.variants.map((v, idx) => ({ id: v.id || `variant-${idx}`, color: v.color || selectedColor, colorCode: v.colorCode || selectedColorCode || '#CCCCCC', image: v.image, inStock: isInStock(v) }));
    } else if (selectedColor) {
      availableColors = [{ id: 'single-color', color: selectedColor, colorCode: selectedColorCode || '#CCCCCC' }];
    }

    const itemId = `${productGroupId}-${selectedColor || 'default'}`;

    setCartItems((prev) => {
      const existing = prev.find((item) => item.productGroupId === productGroupId && item.selectedColor === selectedColor);
      if (existing) return prev.map((item) => (item.cartLineId === existing.cartLineId ? { ...item, quantity: item.quantity + 1 } : item));
      return [...prev, {
        id: itemId, cartLineId: `${itemId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, productGroupId,
        name: productGroup.baseName || productGroup.name, brand: productGroup.brand,
        price: matchingVariant.price || productGroup.price, originalPrice: matchingVariant.originalPrice, sale: matchingVariant.sale,
        image: matchingVariant.image || variantToAdd.image || productGroup.image,
        imageId: selectedImageId, imageUrl: matchingVariant.image, colorName: selectedColorName,
        selectedColor, selectedColorCode, availableColors, quantity: 1,
        material: productGroup.material, category: productGroup.category, inStock: isInStock(matchingVariant),
      }];
    });

    trackUserBehavior('productsClicked', { productId: itemId, productName: productGroup.baseName || productGroup.name, category: productGroup.category, price: matchingVariant.price || productGroup.price, color: selectedColor, action: 'add_to_cart' });
    const cartValue = calculateCartTotal() + (matchingVariant.price || productGroup.price);
    analyticsTracking.trackAddToCart(itemId, 1, matchingVariant.price || productGroup.price, cartValue);
    analyticsTracking.trackFunnelStage('add_to_cart', null, null, null, [itemId], cartValue);

    setAnimatingCartItem(itemId);
    setTimeout(() => setAnimatingCartItem(null), 600);
    setShowQuickAdd(itemId);
    setTimeout(() => setShowQuickAdd(null), 2000);
  };

  const removeFromCart = (cartLineId) => setCartItems((prev) => prev.filter((item) => item.cartLineId !== cartLineId));

  const updateCartItemColor = (cartLineId, colorOption) => {
    setCartItems((prev) => prev.map((item) => {
      if (item.cartLineId !== cartLineId) return item;
      const selectedColorOption = item.availableColors?.find((c) => c.color === colorOption.color);
      return {
        ...item,
        selectedColor: colorOption.color,
        selectedColorCode: colorOption.colorCode || colorOption.color,
        // colorName is what the cart drawer and checkout review actually display
        // (colorName || selectedColor) — it has to be updated here too, or the
        // UI keeps showing whichever color was added first forever.
        colorName: selectedColorOption?.colorName || colorOption.colorName || colorOption.color,
        id: selectedColorOption?.id || `${item.productGroupId}-${colorOption.color}`,
        image: selectedColorOption?.image || selectedColorOption?.imageUrl || item.image,
      };
    }));
  };

  const getPaymentMessage = (paymentMethod) => {
    switch (paymentMethod) {
      case 'CREDIT_CARD': return 'Your card payment is being processed. You will receive a confirmation once completed.';
      case 'BANK_TRANSFER': return 'Please complete the bank transfer using the provided details. Your order will be confirmed once payment is received.';
      case 'PAYPAL': return 'You will be redirected to PayPal to complete your payment.';
      case 'DIGITAL_WALLET': return 'Your digital wallet payment is being processed.';
      default: return 'Your payment is being processed.';
    }
  };

  const pollPaymentStatus = (checkoutRequestID, orderId = null) => {
    const stopPolling = (targetInterval) => { if (targetInterval) clearInterval(targetInterval); setPaymentPollingInterval(null); };
    if (paymentPollingInterval) { clearInterval(paymentPollingInterval); setPaymentPollingInterval(null); }

    let isTerminal = false;
    const interval = setInterval(async () => {
      if (isTerminal) return;
      try {
        const statusUrl = orderId ? `${API_BASE_URL}/api/payments/status/${checkoutRequestID}?orderId=${orderId}` : `${API_BASE_URL}/api/payments/status/${checkoutRequestID}`;
        const dbResponse = await fetch(statusUrl);
        if (dbResponse.ok) {
          const dbResult = await dbResponse.json();
          const payment = dbResult.payment || dbResult;
          if (payment && payment.status) {
            if (payment.status === 'COMPLETED') {
              isTerminal = true; stopPolling(interval);
              setPaymentStatus((prev) => ({ ...prev, status: 'success' }));
              notifications.showSuccess('Payment successful! Your order has been confirmed.');
              const orderNumber = payment.orderNumber || orderId || 'Unknown';
              analyticsTracking.trackPurchase(orderId || payment.orderId || 'Unknown', orderNumber, payment.amount || checkoutTotals.total || 0, cartItems.length, cartItems.map((item) => ({ id: item.id, name: item.name, quantity: item.quantity })));
              analyticsTracking.trackFunnelStage('purchase', 'payment_initiated', null, null, cartItems.map((item) => item.id), payment.amount || checkoutTotals.total || 0);
              setCartItems([]);
              setTimeout(() => { setShowPaymentStatus(false); setCheckoutStep(3); }, 1000);
              return;
            }
            if (payment.status === 'FAILED') {
              isTerminal = true; stopPolling(interval);
              const msg = payment.failureReason || 'Payment failed. Please try again.';
              setPaymentStatus((prev) => ({ ...prev, status: 'failed', failureReason: msg }));
              notifications.showError(msg);
              return;
            }
            if (payment.status === 'PENDING') setPaymentStatus((prev) => ({ ...prev, status: 'pending' }));
            return;
          }
        } else if (dbResponse.status === 404 && orderId) {
          try {
            const orderResponse = await fetch(`${API_BASE_URL}/api/orders/${orderId}`);
            if (orderResponse.ok) {
              const fetchedOrder = await orderResponse.json();
              const latestPayment = fetchedOrder.payments?.length > 0 ? fetchedOrder.payments[fetchedOrder.payments.length - 1] : null;
              if (fetchedOrder.paymentStatus === 'COMPLETED' || fetchedOrder.status === 'CONFIRMED' || latestPayment?.status === 'COMPLETED') {
                isTerminal = true; stopPolling(interval);
                setPaymentStatus((prev) => ({ ...prev, status: 'success' }));
                notifications.showSuccess('Payment successful! Your order has been confirmed.');
                setCartItems([]);
                setTimeout(() => { setShowPaymentStatus(false); setCheckoutStep(3); }, 1000);
                return;
              }
            }
          } catch (orderErr) { /* ignore */ }
        }

        const response = await fetch(`${API_BASE_URL}/api/mpesa/query`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ checkoutRequestID }) });
        const result = await response.json();
        const rawCode = result?.result?.ResultCode ?? result?.result?.resultCode ?? null;
        const code = rawCode !== null && rawCode !== undefined ? parseInt(rawCode, 10) : null;

        if (result.success && result.result && code === 0) {
          isTerminal = true; stopPolling(interval);
          setPaymentStatus((prev) => ({ ...prev, status: 'success' }));
          notifications.showSuccess('Payment successful! Your order has been confirmed.');
          setCartItems([]);
          setTimeout(() => { setShowPaymentStatus(false); setCheckoutStep(3); }, 500);
        } else if (result.success && result.result && code === 1) {
          setPaymentStatus((prev) => ({ ...prev, status: 'pending', mpesaResultCode: code, message: 'Transaction is being processed by M-Pesa. Please wait...' }));
        } else if (result.success && result.result && code === 1032) {
          setPaymentStatus((prev) => ({ ...prev, status: 'pin_pending', mpesaResultCode: code, message: 'Please complete M-Pesa PIN authorization on your phone.' }));
        } else if (result.success && result.result && code !== null && code !== 0 && code !== 1 && code !== 1032) {
          if (code === 2003) {
            isTerminal = true; stopPolling(interval);
            setPaymentStatus((prev) => ({ ...prev, status: 'insufficient_balance', lastResultCode: code, failureReason: 'Insufficient M-Pesa balance. Please top up your wallet and check payment status again.', message: 'Insufficient M-Pesa balance. Please top up your wallet and check payment status again.' }));
            notifications.showWarning('Insufficient M-Pesa balance. Top up your wallet and tap "Check again".');
            return;
          }
          isTerminal = true; stopPolling(interval);
          let errorMessage = 'Payment failed. Please try again.';
          switch (code) {
            case 1037: errorMessage = 'M-Pesa timeout: Phone not reachable. Please ensure your phone is on and connected to network, and the number is registered with M-Pesa.'; break;
            case 1032: errorMessage = 'M-Pesa timeout: Please enter your M-Pesa PIN when prompted on your phone.'; break;
            case 2001: errorMessage = 'Wrong PIN entered. Please try again.'; break;
            case 2002: errorMessage = 'Transaction cancelled by user.'; break;
            case 2004: errorMessage = 'M-Pesa account not found. Please ensure the number is registered with M-Pesa.'; break;
            case 2029: errorMessage = 'Payment gateway configuration issue (2029). Our M-Pesa account may need verification. Please contact support or try again in a few moments.'; break;
            default: break;
          }
          setPaymentStatus((prev) => ({ ...prev, status: 'failed', failureReason: errorMessage, resultCode: code }));
          notifications.showError(errorMessage);
        } else if (!result.success) {
          setPaymentStatus((prev) => ({ ...prev, status: 'pending', message: 'Waiting for payment confirmation from M-Pesa...' }));
        }
      } catch (error) { /* keep polling */ }
    }, 3000);

    setPaymentPollingInterval(interval);
    setTimeout(() => {
      if (!isTerminal && interval) { isTerminal = true; stopPolling(interval); setPaymentStatus((prev) => ({ ...prev, status: 'timeout' })); }
    }, 300000);
  };

  const addToRecentlyViewed = (productGroup) => {
    const selectedVariant = getSelectedVariant(productGroup);
    if (!selectedVariant || !selectedVariant.id) return;
    trackUserBehavior('productsViewed', { productId: selectedVariant.id, productName: productGroup.baseName || productGroup.name, category: productGroup.category, price: selectedVariant.price, color: selectedVariant.color, material: productGroup.material, brand: productGroup.brand });
    setViewedProducts((prev) => [productGroup, ...prev.filter((p) => p.id !== productGroup.id)].slice(0, 12));
  };

  const handleQuickPreview = (productGroup) => {
    setQuickPreviewProduct(productGroup);
    const variant = getSelectedVariant(productGroup);
    analyticsTracking.trackProductView(variant.id, productGroup.baseName || productGroup.name, variant.price, productGroup.category);
    analyticsTracking.trackFunnelStage('product_view', 'visit', null, null, [variant.id], null);
    addToRecentlyViewed(productGroup);
  };

  const updateQuantity = (cartLineId, newQuantity) => {
    if (newQuantity <= 0) { removeFromCart(cartLineId); return; }
    setCartItems((prev) => prev.map((item) => (item.cartLineId === cartLineId ? { ...item, quantity: newQuantity } : item)));
  };

  const toggleWishlist = (productId) => {
    setWishlist((prev) => {
      const next = new Set(prev);
      next.has(productId) ? next.delete(productId) : next.add(productId);
      return next;
    });
  };

  const enabledStorefrontCategories = useMemo(() => ['handbags'], []);

  // Products for the current storefront type (Handbags/Shoes/...), before the
  // finer-grained category pills below are applied — this is also the base
  // set the pills' counts are computed from.
  const storefrontTypeProducts = useMemo(() => {
    if (!enabledStorefrontCategories.includes(selectedStorefrontCategory)) return [];
    return displayedProducts.filter((productGroup) => selectedStorefrontCategory === 'handbags' ? true : (productGroup.category || '').toLowerCase().includes(selectedStorefrontCategory));
  }, [displayedProducts, enabledStorefrontCategories, selectedStorefrontCategory]);

  // Client-side category-pill filter — no network round trip, so switching
  // categories is instant and never touches the page-wide `loading` flag.
  const visibleCatalogProducts = useMemo(() => {
    if (selectedCategory === 'all') return storefrontTypeProducts;
    return storefrontTypeProducts.filter((productGroup) => productGroup.categoryId === selectedCategory);
  }, [storefrontTypeProducts, selectedCategory]);

  const availableBrands = useMemo(() => [...new Set(visibleCatalogProducts.map((p) => p.brand).filter(Boolean))], [visibleCatalogProducts]);
  const availableMaterials = useMemo(() => [...new Set(visibleCatalogProducts.map((p) => p.material).filter(Boolean))], [visibleCatalogProducts]);

  const filteredProducts = useMemo(() => {
    const filtered = visibleCatalogProducts.filter((productGroup) => {
      const priceMatch = productGroup.variants.some((v) => v.price >= priceFilter[0] && v.price <= priceFilter[1]);
      const searchMatch = searchQuery === '' || [productGroup.baseName || productGroup.name, productGroup.brand, productGroup.description, productGroup.material].filter(Boolean).some((f) => f.toLowerCase().includes(searchQuery.toLowerCase()));
      const brandMatch = advancedFilters.brand === 'all' || productGroup.brand === advancedFilters.brand;
      const materialMatch = advancedFilters.material === 'all' || productGroup.material === advancedFilters.material;
      const stockMatch = !advancedFilters.inStockOnly || productGroup.variants.some((v) => isInStock(v));
      const ratingMatch = productGroup.variants.some((v) => (v.rating || 0) >= advancedFilters.minRating);
      return priceMatch && searchMatch && brandMatch && materialMatch && stockMatch && ratingMatch;
    });
    switch (advancedFilters.sortBy) {
      case 'price-low': return [...filtered].sort((a, b) => getSelectedVariant(a).price - getSelectedVariant(b).price);
      case 'price-high': return [...filtered].sort((a, b) => getSelectedVariant(b).price - getSelectedVariant(a).price);
      case 'rating': return [...filtered].sort((a, b) => (getSelectedVariant(b).rating || 0) - (getSelectedVariant(a).rating || 0));
      default: return filtered;
    }
  }, [visibleCatalogProducts, priceFilter, searchQuery, advancedFilters]);

  // Counts reflect what's actually on screen for the current storefront type
  // (active products only, pre-pill-filter) instead of the server's raw
  // per-category totals, so a pill's count always matches what clicking it reveals.
  const processedCategories = useMemo(() => {
    if (!categories.length) return [];
    const allCategory = { id: 'all', name: 'All Handbags', count: storefrontTypeProducts.length };
    const apiCategories = categories.map((cat) => ({
      id: cat.id,
      name: cat.name,
      count: storefrontTypeProducts.filter((p) => p.categoryId === cat.id).length,
    }));
    return [allCategory, ...apiCategories];
  }, [categories, storefrontTypeProducts]);

  const onSearchChange = (value) => {
    setSearchQuery(value);
    if (value.length > 2) trackUserBehavior('searchQueries', value);
  };

  const onSelectCategory = (id) => setSelectedCategory(id);
  const onFooterSelectCategory = (id) => { onSelectCategory(id); scrollToSection('products'); };

  return (
    <div className="min-h-screen bg-paper">
      <Header
        activeSection={activeSection}
        scrollToSection={scrollToSection}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
        onOpenCart={() => setCartOpen(true)}
        onAdminLogin={handleAdminLogin}
      />

      <Hero
        loading={loading}
        error={error}
        displayedSlides={displayedSlides}
        currentSlide={currentSlide}
        setCurrentSlide={setCurrentSlide}
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        mousePosition={mousePosition}
        handleMouseMove={handleMouseMove}
        setMousePosition={setMousePosition}
        sectionsRef={sectionsRef}
        onSlideAction={handleSlideAction}
      />
      <TrustBar />

      <ProductGrid
        sectionRef={(el) => { sectionsRef.current.products = el; }}
        selectedStorefrontCategory={selectedStorefrontCategory}
        onSelectStorefrontCategory={setSelectedStorefrontCategory}
        processedCategories={processedCategories}
        selectedCategory={selectedCategory}
        onSelectCategory={onSelectCategory}
        priceFilter={priceFilter}
        setPriceFilter={setPriceFilter}
        advancedFilters={advancedFilters}
        setAdvancedFilters={setAdvancedFilters}
        availableBrands={availableBrands}
        availableMaterials={availableMaterials}
        filteredProducts={filteredProducts}
        animatingCartItem={animatingCartItem}
        showQuickAdd={showQuickAdd}
        onQuickView={handleQuickPreview}
        onAddToCart={addToCart}
        viewedProducts={viewedProducts}
        showRecentlyViewed={showRecentlyViewed}
        setShowRecentlyViewed={setShowRecentlyViewed}
        recommendations={recommendations}
      />

      <BrandStory sectionsRef={sectionsRef} onOpenContact={() => setShowContactPage(true)} />

      <Footer
        categories={processedCategories.filter((c) => c.id !== 'all')}
        onScrollToSection={scrollToSection}
        onSelectCategory={onFooterSelectCategory}
      />

      <QuickAddToast visible={Boolean(showQuickAdd)} onViewCart={() => setCartOpen(true)} />
      <ImageZoomModal image={zoomImage} onClose={() => setZoomImage(null)} />

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        items={cartItems}
        onUpdateQuantity={updateQuantity}
        onUpdateColor={updateCartItemColor}
        subtotal={calculateCartTotal()}
        onCheckout={() => { setCartOpen(false); handleCheckout(); }}
      />

      {quickPreviewProduct && (
        <ProductQuickView
          product={quickPreviewProduct}
          onClose={() => setQuickPreviewProduct(null)}
          onAddToCart={(product) => { addToCart(product); setQuickPreviewProduct(null); }}
          isWishlisted={wishlist.has(quickPreviewProduct.id)}
          onToggleWishlist={toggleWishlist}
        />
      )}

      <ContactModal
        open={showContactPage}
        onClose={() => setShowContactPage(false)}
        formData={contactFormData}
        onChange={handleContactInputChange}
        errors={contactFormErrors}
        onSubmit={handleContactSubmit}
        isSubmitting={isSubmittingContact}
      />

      <CheckoutModal
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        checkoutStep={checkoutStep}
        cartItems={cartItems}
        checkoutTotals={checkoutTotals}
        checkoutFormData={checkoutFormData}
        inputHandlers={inputHandlers}
        formErrors={formErrors}
        handleLocationChange={handleLocationChange}
        locationEntryMode={locationEntryMode}
        setLocationEntryMode={setLocationEntryMode}
        manualMapRef={manualMapRef}
        applySearchResultRef={applySearchResultRef}
        locationPrecisionWarning={locationPrecisionWarning}
        onContinue={handleContinueToPayment}
        isSubmitting={isSubmitting}
        onBack={() => setCheckoutStep(1)}
        onSubmit={validateAndSubmitCheckout}
        onContinueShopping={() => { setShowCheckout(false); setCheckoutStep(1); }}
      />

      {showPaymentStatus && paymentStatus && (
        <PaymentStatusModal
          paymentStatus={paymentStatus}
          onCheckAgain={() => checkPaymentOnce(paymentStatus.checkoutRequestID, paymentStatus.orderId)}
          onCancel={() => {
            if (paymentPollingInterval) clearInterval(paymentPollingInterval);
            setPaymentPollingInterval(null);
            setShowPaymentStatus(false);
          }}
          onGoToConfirmation={() => { setShowPaymentStatus(false); setCheckoutStep(3); setCartItems([]); }}
          onRetry={() => { setShowPaymentStatus(false); setCheckoutStep(2); }}
          onCloseFailed={() => { setShowPaymentStatus(false); setShowCheckout(false); }}
          onUseDifferentMethod={() => { inputHandlers.paymentMethod(''); setShowPaymentStatus(false); }}
          onBackToPayment={() => { setShowPaymentStatus(false); setCheckoutStep(2); }}
        />
      )}

      {showAdminLogin && (
        <AdminLogin onClose={() => setShowAdminLogin(false)} onLoginSuccess={handleAdminLoginSuccess} initialError={adminLoginInitialError} />
      )}

      <CookieBanner />
    </div>
  );
};

export default AdvancedEcommerceWebsite;
